'use client';

/**
 * FacetCard — one search facet, as a card.
 *
 * Uniform envelope (title · active-count · collapse chevron) + a BODY chosen by
 * the facet's data shape, not hand-built per facet:
 *   - ranked-rows  (enum / multi / ref) — checkbox rows, ranked, right-aligned
 *     count, with a magnitude bar drawn only when the distribution isn't
 *     dominated by one value (a lone 227-vs-slivers bar is decorative).
 *   - range        (range) — a compact min–max span (slider is a follow-up).
 * Swatches (Colour) are deferred: `primary_color` is a mixed vocab (real colours
 * next to material/descriptor words like "oak"/"natural") that isn't swatch-clean
 * yet, so Colour rides ranked-rows for now.
 *
 * A card (registered/exported), so a facet can relocate off the rail later — the
 * grip/drag affordance lands with that, not before. Thin painter: values +
 * counts are shipped; selection comes from the store; accordion state from the
 * parent.
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain canvas
 */

import { useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';

export interface FacetValue {
  value: string;
  label?: string;
  count: number;
  /** primary_color only — the bucket's own measured centroid swatch,
   *  the exact colour the backend's ΔE filter matches against (not a
   *  CSS-name guess from the value string). */
  swatch?: string;
}

export interface Facet {
  key: string;
  label: string;
  type: 'enum' | 'multi' | 'range' | 'ref';
  tier: 'spine' | 'tail';
  values?: FacetValue[];
  min?: number;
  max?: number;
}

const PEEK = 6; // values shown before "+ N more"
const DOMINATED = 0.6; // one value ≥ 60% of the total → bars go flat, skip them

export function FacetCard({
  facet,
  selected,
  open,
  onToggleOpen,
  onToggle,
  moreLabel,
}: {
  facet: Facet;
  selected: string[];
  open: boolean;
  onToggleOpen: () => void;
  onToggle: (value: string) => void;
  moreLabel: string; // "{n} more" — chrome from the BFF label lane
}) {
  const [showAll, setShowAll] = useState(false);

  useCardExport(
    useMemo(() => ({
      label: facet.label,
      content_key: 'product_facet',
      surface: 'canvas' as const,
      status: 'loaded' as const,
      query_key: null,
      text: `${facet.label}${selected.length ? ` · ${selected.length}` : ''}`,
      json: { key: facet.key, tier: facet.tier, type: facet.type, selected },
    }), [facet, selected]),
  );

  return (
    <div
      className={cn(
        // Each facet is its OWN recessed card (guide's facet costume) — a muted
        // plane so Vendor / Colour / Material read as distinct items on the
        // chrome rail, not one merged accordion. Rows inside stay flat.
        'rounded-lg px-2.5 py-2 transition-colors',
        selected.length > 0
          ? 'bg-primary/10 shadow-[inset_3px_0_0_hsl(var(--primary))]'
          : 'bg-muted',
      )}
    >
      {/* Uniform envelope header */}
      <button
        onClick={onToggleOpen}
        className="flex w-full items-center gap-2 text-left"
      >
        <span className={cn(text.muted, 'flex-1 truncate font-medium')}>{facet.label}</span>
        {selected.length > 0 && (
          <span className="rounded-full bg-primary/15 px-1.5 text-[10px] font-medium text-primary tabular-nums">
            {selected.length}
          </span>
        )}
        <ChevronDown
          className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', !open && '-rotate-90')}
        />
      </button>

      {open && (
        <div className="mt-1">
          {facet.type === 'range' ? (
            <RangeBody facet={facet} />
          ) : (
            <RankedRows
              facet={facet}
              selected={selected}
              onToggle={onToggle}
              showAll={showAll}
              onShowAll={() => setShowAll(true)}
              moreLabel={moreLabel}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** ranked-rows — checkbox rows, ranked, count right-aligned; magnitude bar only
 *  when the distribution isn't dominated by a single value. */
function RankedRows({
  facet,
  selected,
  onToggle,
  showAll,
  onShowAll,
  moreLabel,
}: {
  facet: Facet;
  selected: string[];
  onToggle: (value: string) => void;
  showAll: boolean;
  onShowAll: () => void;
  moreLabel: string;
}) {
  const values = facet.values ?? [];
  const shown = showAll ? values : values.slice(0, PEEK);
  const hidden = values.length - shown.length;
  const total = values.reduce((s, v) => s + v.count, 0) || 1;
  const max = Math.max(...values.map((v) => v.count), 1);
  const dominated = max / total > DOMINATED; // skip decorative bars

  return (
    <div className="space-y-0.5">
      {shown.map((v) => {
        const on = selected.includes(v.value);
        return (
          <button
            key={v.value}
            onClick={() => onToggle(v.value)}
            className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-muted/50"
          >
            <span
              className={cn(
                'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border',
                on ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
              )}
            >
              {on && <Check className="h-2.5 w-2.5" />}
            </span>
            <span className={cn('flex-1 truncate', on ? 'text-foreground' : text.muted)}>
              {v.label ?? v.value}
            </span>
            {!dominated && (
              <span className="h-1 w-8 shrink-0 overflow-hidden rounded-full bg-muted">
                <span className="block h-full rounded-full bg-primary/40" style={{ width: `${(v.count / max) * 100}%` }} />
              </span>
            )}
            <span className={cn(text.muted, 'w-8 shrink-0 text-right tabular-nums')}>{v.count}</span>
          </button>
        );
      })}
      {hidden > 0 && (
        <button onClick={onShowAll} className={cn(text.muted, 'px-1 hover:text-foreground')}>
          {moreLabel.replace('{n}', String(hidden))}
        </button>
      )}
    </div>
  );
}

/** range — compact min–max span. Read-only for now; a slider is the follow-up
 *  once the range-filter write path is wired. */
function RangeBody({ facet }: { facet: Facet }) {
  const fmt = (n?: number) => (n == null ? '—' : n.toLocaleString());
  return (
    <div className={cn(text.muted, 'flex items-center gap-2 px-1 tabular-nums')}>
      <span>{fmt(facet.min)}</span>
      <span className="h-px flex-1 bg-border" />
      <span>{fmt(facet.max)}</span>
    </div>
  );
}
